/**
 * Consultation-only cart sweep (COM-01, Paket 4) — loaded globally from
 * layout/theme.liquid, next to bundle-cart-guard.js, on the shared
 * `#cart-line-sweep` module (which also serves zero-price-cart-guard.js).
 *
 * Products flagged `custom.beratung_erforderlich` have no add-to-cart anywhere
 * in this theme (PDP, quick view, cards, configurators). A line can therefore
 * only reach the cart through a hand-built request (`GET /cart/add?id=…`,
 * `POST /cart/add.js`, a shared cart link). Until 2026-09-05 the theme merely
 * disabled its own checkout buttons for such a cart, which left the direct
 * `/checkout` URL open (BER-01-D1, P0). This guard removes every flagged line
 * server-side (`/cart/change`, quantity 0) as soon as the cart UI shows one, so
 * a customer who lands on any theme page with such a line loses it before they
 * can reach checkout, and is told why.
 *
 * Scope, honestly stated: client-side only. A checkout permalink
 * (`/cart/<variant>:<qty>`) or a script that never loads a theme page still
 * reaches checkout; that is the job of the server-side Cart & Checkout
 * Validation (Function Studio rule COM-01), not of this file. The guard never
 * touches lines whose product is not flagged, never blocks a draft-order
 * invoice checkout (that never involves the online cart), and never breaks the
 * page — every path swallows its own errors.
 *
 * Signal: the drawer (snippets/cart-drawer-item.liquid) and the cart page
 * (sections/main-cart-items.liquid) render `[data-consultation-line="<key>"]`
 * on the "Nur mit Beratung" badge of each flagged line. Liquid decides what is
 * flagged; this file only reads keys. Trigger design, sweep mechanics and the
 * notice live in assets/cart-line-sweep.js.
 *
 * Entry point only — nothing imports this file; the exports exist for
 * tests/consultation-cart-guard.test.mjs. Side effects live in boot().
 */

import { collectMarkedLines, removeLines, formatRemovedNotice, createSweeper, bootLineGuard } from '#cart-line-sweep';

export { removeLines, formatRemovedNotice, createSweeper };

const SOURCE = 'consultation-cart-guard';
const LINE_ATTR = 'data-consultation-line';
const TITLE_ATTR = 'data-consultation-title';

/** Reads the server-rendered markers. Pure, deduplicated by line key. */
export function collectFlaggedLines(doc) {
  return collectMarkedLines(doc, { attr: LINE_ATTR, titleAttr: TITLE_ATTR });
}

function boot() {
  bootLineGuard({
    source: SOURCE,
    attr: LINE_ATTR,
    titleAttr: TITLE_ATTR,
    noticeAttr: 'data-consultation-removed',
    noticeClass: 'cart-drawer__consultation-notice',
    noticeTemplate: () => window.cartStrings?.consultationRemoved,
  });
}

if (typeof document !== 'undefined') boot();
